import { List, useListRef } from 'react-window';

import type { RenderItemFn } from '../types';

import type { RowProps } from './Row';
import { RowComponent } from './Row';

export interface VirtualizedListProps<ItemProps = unknown> {
  className?: string;
  data: Array<Omit<ItemProps, 'index'>>;
  'data-testid'?: string;
  /** Specify the height (in px) of a single item. */
  itemHeight: number;
  /** Specify the maximum height (in px) that the container is supposed to take. */
  height: number;
  /** Extra item to highlight */
  highlighted?: {
    idx: number;
    height: number;
  };
  onItemsRendered?: (args: { startIndex: number; stopIndex: number }) => void;
  renderItem: RenderItemFn<ItemProps>;
  getItemSize?: (index: number) => number;
}

const _getItemSize =
  (highlighted: VirtualizedListProps['highlighted'], itemHeight: number) =>
  (idx: number): number => {
    if (highlighted && highlighted.idx > -1) {
      return idx === highlighted?.idx ? highlighted.height : itemHeight;
    }

    return itemHeight;
  };

/**
 * Component that handles rendering of huge lists using virtualization techniques.
 * In order to achieve the most performance out of it you should provide `renderItem` prop as a memoized item in order to avoid unnecessary re-renders.
 */
export const VirtualizedList = <ItemProps,>({
  className,
  data,
  'data-testid': dataTestId,
  height,
  itemHeight,
  highlighted,
  getItemSize = _getItemSize(highlighted, itemHeight),
  onItemsRendered,
  renderItem,
}: VirtualizedListProps<ItemProps>) => {
  const ref = useListRef(null);

  return (
    <ul className="overflow-y-auto" style={{ height, width: '100%' }} data-testid={dataTestId}>
      <List<RowProps<ItemProps>>
        listRef={ref}
        rowHeight={getItemSize}
        className={className}
        onRowsRendered={onItemsRendered}
        rowCount={data.length}
        rowProps={{ data, renderItem }}
        rowComponent={RowComponent}
      />
    </ul>
  );
};
